import {
  Translate,
  AlignParameters,
  Scale,
  _Scale,
} from '../../../../../elements/resource/types/shape.type';
import {
  BaseTexture,
  BlurFilter,
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Texture,
} from 'pixi.js';
import {
  StudioAnimation,
  StudioCell,
  StudioImageShapeDescriptor,
} from '../../studio.types';
import { selectMedia, selectShape } from '../../studio.actions';
import { DragResponse } from '../../../../../elements/util/point-controller/point-controller';
import { RectangleController } from '../../../../../elements/util/rectangle-controller/rectangle-controller';
import { StudioSceneShape } from '../../../../shape/shapes/general/root/studio-scene-shape';
import { StudioShape, StudioShapeConfig } from '../studio-shape';
import { RectangleElement } from '../../../../shape/shapes/primitive/rectangle-element';
import gsap from 'gsap';

export class StudioImageShape extends StudioShape<StudioImageShapeDescriptor> {
  private sprite: Sprite;
  private texture: Texture;
  private gradientMaskSprite: Sprite | null = null;
  private _img: HTMLImageElement;
  private _radiusMask: Graphics;

  container: Container;
  private _width: number;
  private _height: number;
  private _scale: { x: number; y: number } = { x: 1, y: 1 };
  private _translate: Translate = { x: 0, y: 0 };
  get s3Id() {
    return this.image.s3Id;
  }
  get cs() {
    return this.shape.cs;
  }
  set s3Id(val: string) {
    this.image.s3Id = val;
  }
  get cell() {
    return this.descriptor.cell;
  }
  getType(): string {
    return 'image-shape';
  }
  get rWidth() {
    return this._width * this._scale.x;
  }
  get rHeight() {
    return this._height * this._scale.y;
  }
  initialised;
  get image() {
    return (
      this.studioService.images[this.descriptor.imageId] ||
      this.studioService.images[this.id]
    );
  }

  get studioService() {
    return this.shape.studioService;
  }

  loadPromise: Promise<void> = Promise.resolve();

  maskRectangle: RectangleElement;
  constructor(
    shape: StudioSceneShape,
    id: string,
    data: StudioImageShapeDescriptor,
    public loaded?: () => void,
    config?: StudioShapeConfig,
  ) {
    super(shape, id, data, config);
    this.loadPromise = this.init();
  }

  async init() {
    // console.log('init-image', this.image);
    this.container = new Container();
    this.shape.container.addChild(this.container);
    const id = this.id;

    if (!this.image) {
      return;
    }
    // -- // -- // -- //
    const imageUrl = id?.startsWith('http')
      ? id
      : this.image.url || `https://private.cdn.inwedio.com/images/${id}`;

    await this.initImagePreview(imageUrl);
  }

  private async initImagePreview(imageUrl: string) {
    return new Promise<void>(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        this._img = img;
        this.createImageSprite(img);
        this.loaded?.();
        resolve();
      };

      img.onerror = () => {
        console.error('Failed to load image:', imageUrl);
        this.createPlaceholder();
      };

      img.src = imageUrl;
    });
  }

  private buildTexture(img: HTMLImageElement): Texture {
    const { colorize, crop } = this.descriptor;
    const base = colorize
      ? this.makeColorizedTexture(img, colorize)
      : Texture.from(img);

    if (!crop) return base;

    const frame = new Rectangle(
      Math.round(crop.x * img.naturalWidth),
      Math.round(crop.y * img.naturalHeight),
      Math.round(crop.w * img.naturalWidth),
      Math.round(crop.h * img.naturalHeight),
    );
    return new Texture(base.baseTexture, frame);
  }

  private createImageSprite(img: HTMLImageElement) {
    this.sprite = new Sprite(this.buildTexture(img));
    this.container.addChild(this.sprite);

    const { crop } = this.descriptor;
    this._width = Math.round(
      (crop ? crop.w * img.naturalWidth : img.naturalWidth) || 320,
    );
    this._height = Math.round(
      (crop ? crop.h * img.naturalHeight : img.naturalHeight) || 180,
    );

    this.doAlign(this.cell);

    // Create mask rectangle after sprite is ready
    this.initMaskRectangle();
    // this.applyRadiusMask();

    this.container.visible = true;
    this.initialised = true;

    this.initRC();
  }

  // applyRadiusMask() {
  //   if (!this.sprite) return;
  //   const radius = this.descriptor.radius;
  //   if (!radius) {
  //     this.sprite.mask = this.maskRectangle?.element ?? null;
  //     return;
  //   }
  //   const [w, h] = this.grossDims;
  //   if (!this._radiusMask) {
  //     this._radiusMask = new Graphics();
  //     this.container.addChild(this._radiusMask);
  //   }
  //   this._radiusMask.clear();
  //   this._radiusMask.beginFill(0xffffff);
  //   this._radiusMask.drawRoundedRect(0, 0, w, h, radius);
  //   this._radiusMask.endFill();
  //   this.sprite.mask = this._radiusMask;
  // }

  makeColorizedTexture(imgEl: HTMLImageElement, color: string) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);

    const c = document.createElement('canvas');
    c.width = imgEl.naturalWidth;
    c.height = imgEl.naturalHeight;

    const ctx = c.getContext('2d')!;
    ctx.drawImage(imgEl, 0, 0);

    const im = ctx.getImageData(0, 0, c.width, c.height);
    const d = im.data;

    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] !== 0) {
        d[i + 0] = r;
        d[i + 1] = g;
        d[i + 2] = b;
      }
    }

    ctx.putImageData(im, 0, 0);

    return Texture.from(c);
  }

  initMaskRectangle() {
    if (this.maskRectangle) {
      this.maskRectangle.remove();
    }
    // console.log('init-mask-rectangle', this.sprite, this.descriptor.radius);
    if (!this.sprite) {
      return;
    }
    const [width, height] = this.grossDims;
    this.maskRectangle = new RectangleElement(this, this.container, {
      position: { x: this.sprite.x, y: this.sprite.y },
      width: this._width * this._scale.x,
      height: this._height * this._scale.y,
      fill: '#000000',
      opacity: 0.001,
      r: this.descriptor.radius || 0,
    });
    this.sprite.mask = this.maskRectangle.element;
  }

  private createPlaceholder() {
    // Create a simple placeholder graphic
    console.warn('Creating placeholder for failed image load');
    this._width = 320;
    this._height = 180;
    this.container.visible = true;
    this.initialised = true;
    // -- //
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  doAlign(cell: StudioCell, _params: AlignParameters = {}): Translate {
    if (!cell) {
      return;
    }

    this.coverAlign();
    return;
  }

  private coverAlign() {
    const width = this._width;
    const height = this._height;

    const [w, h] = this.netDims;
    const widthRatio = w / width;
    const heightRatio = h / height;

    const scale = Math.min(widthRatio, heightRatio);

    this._scale.x = scale;
    this._scale.y = scale;

    this.applyScale(this._scale);

    const [grossWidth, grossHeight] = this.grossDims;

    // console.log('---- image dx dy ----', dx, dy);
    const actualWidth = width * scale;
    const actualHeight = height * scale;
    this.sprite?.position.set(
      (grossWidth - actualWidth) / 2,
      (grossHeight - actualHeight) / 2,
    );

    this.maskRectangle?.patch({
      position: { x: this.sprite.x, y: this.sprite.y },
      width: actualWidth,
      height: actualHeight,
    });

    const [x0, y0] = this.grossCoords;
    this.setPosition(x0, y0);
  }

  rc: RectangleController;
  private unmovedDims: [number, number];
  private draggedDims: [number, number];

  unMovedScale: { x: number; y: number };
  unMovedTranslate: { x: number; y: number };
  initRC() {
    if (!this.initialised) {
      return;
    }

    const [width, height] = this.grossDims;
    this.rc = new RectangleController(
      this as any,
      {
        id: `image-${this.descriptor.imageId}`,
        width,
        // offset: [this.container.x, this.container.y],
        height,
        noStroke: false,
        fill: true,
        hiddenMode: true,
        noScale: false,
        fillOpacity: 0.001,
        'stroke-width': 1,
        startDrag: () => {
          this.unmovedDims = [width, height];
          console.log('startDrag', this.unmovedDims);
          console.log('startDrag.scale', this._scale);
          this.unMovedScale = { ...this._scale };
          this.unMovedTranslate = { x: this.container.x, y: this.container.y };
        },
        drag: resp => {
          const { x, y, dx, dy, width: newWidth, height: newHeight } = resp;
          this.draggedDims = [newWidth, newHeight];

          console.log('drag', x, y, newWidth, newHeight);
          console.log('drag > dx dy', dx, dy);
          // this.container.x = x;
          // this.container.y = y;
          const { x: x0, y: y0 } = this.unMovedTranslate;
          this.cell.x = (x0 + x) / this.cs.currentScreenX;
          this.cell.y = (y0 + y) / this.cs.currentScreenY;
          this.cell.w = newWidth / this.cs.currentScreenX;
          this.cell.h = newHeight / this.cs.currentScreenY;

          this.doAlign(this.cell);
          // Update image shape dimensions during drag
          // if (this.cell) {
          //   const scaleX = newWidth / width;
          //   const scaleY = newHeight / height;
          //   console.log('scaleX', scaleX, 'scaleY', scaleY);
          //   const { x, y } = this.unMovedScale;
          //   this._scale.x = x * scaleX;
          //   this._scale.y = y * scaleY;
          //   this.applyScale(this._scale);
          // }

          // console.log('newWidth', newWidth, 'newHeight', newHeight);

          this.maskRectangle.patch({
            width: newWidth,
            height: newHeight,
          });
        },
        endDrag: () => {
          if (!this.draggedDims || !this.unmovedDims) {
            return;
          }

          this.descriptor.cell.custom = true;
          this.save();

          // Reset dragged dims
          this.draggedDims = null;
        },
        clicked: () => this.onImageClicked(),
        mouseover: () => this.onMouseOver(),
        mouseout: () => this.onMouseOut(),
      },
      this.circleContainer,
      this.circleContainer,
    );

    // this.rc.rect.patch({
    //   stroke: '#0191f3',
    //   'stroke-width': 0,
    // });

    this.rc.show({ withoutPCs: true });
    this.onMouseOut();
  }

  update(descriptor: StudioImageShapeDescriptor) {
    const needsRebuild =
      descriptor.colorize !== this.descriptor.colorize ||
      JSON.stringify(descriptor.crop) !== JSON.stringify(this.descriptor.crop);
    this.descriptor = descriptor;

    if (needsRebuild && this._img && this.sprite) {
      this.sprite.texture = this.buildTexture(this._img);
      const { crop } = descriptor;
      this._width = Math.round(
        (crop ? crop.w * this._img.naturalWidth : this._img.naturalWidth) ||
          320,
      );
      this._height = Math.round(
        (crop ? crop.h * this._img.naturalHeight : this._img.naturalHeight) ||
          180,
      );
    }

    this.initMaskRectangle();
    this.coverAlign();
  }

  private onImageClicked() {
    console.log('StudioImageShape clicked:', this.descriptor.imageId);

    this.rc?.show();
    this.shape.store.dispatch(selectShape({ id: this.id }));

    if (this.image) {
      this.shape.store.dispatch(selectMedia({ id: this.image.id }));
    }
  }

  private onMouseOver() {
    if (this.rc) {
      this.rc.rect.patch({ 'stroke-width': 1 });
    }
  }

  private onMouseOut() {
    if (this.rc) {
      this.rc.rect.patch({ 'stroke-width': 0 });
    }
  }

  get dims() {
    return [this._width * this._scale.x, this._height * this._scale.y];
  }

  rcDrag(resp: DragResponse) {
    const { dx, dy } = resp;
    this._translate.x += dx;
    this._translate.y += dy;
    this.container.position.set(this._translate.x, this._translate.y);
  }

  endRCDrag() {
    // Save translate if needed
  }

  applyScale(scale: Scale) {
    if (!this.sprite) return;
    this._scale = scale as _Scale;
    this.sprite.scale = scale as _Scale;
    // this.container.scale = scale as _Scale;
  }

  applyTranslate(translate: Translate) {
    this._translate = translate;
    this.container.position = translate;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  remove(_params?: { noSave?: boolean }) {
    if (this.sprite) {
      this.sprite.destroy();
      this.sprite = null;
    }
    if (this.texture) {
      this.texture.destroy();
      this.texture = null;
    }
    super.remove();
  }

  resetAnimation() {
    if (this.maskRectangle?.element) {
      gsap.killTweensOf(this.maskRectangle.element);
    }
    if (this.gradientMaskSprite) {
      gsap.killTweensOf(this.gradientMaskSprite);
      this.gradientMaskSprite.parent?.removeChild(this.gradientMaskSprite);
      this.gradientMaskSprite.destroy();
      this.gradientMaskSprite = null;
    }

    this.sprite.mask = null;
    this.maskRectangle?.remove();
    this.maskRectangle = null;
  }

  gsapAnimateStep(animation: StudioAnimation): gsap.core.Timeline {
    const timeline = gsap.timeline();

    if (!animation) {
      return timeline;
    }

    const { mask, gradientMask, zoom, duration, type, translate } = animation;
    const [width, height] = this.grossDims;

    if (type === 'appear') {
      if (!mask) {
        this.container.alpha = 0;
        timeline.add(gsap.to(this.container, { alpha: 1, duration }), 0);
      }
    }

    if (type === 'disappear') {
      timeline.add(gsap.to(this.container, { alpha: 0, duration }), 0);
    }

    if (zoom) {
      const { from, to, duration: zoomDuration, translate } = zoom;
      const [zoomDx, zoomDy] = translate || [0, 0];
      if (from !== undefined) {
        const currentWidth = this._width * this._scale.x;
        const currentHeight = this._height * this._scale.y;

        const { w, h } = this.cellDims;

        const dx = ((1 - from) * w) / 2;
        const dy = ((1 - from) * h) / 2;

        this.container.scale.x = from;
        this.container.scale.y = from;

        this.container.position.x += dx - zoomDx;
        this.container.position.y += dy - zoomDy;

        timeline.add(
          gsap.to(this.container.position, {
            duration: zoomDuration || duration || 1,
            x: (i, target) => {
              // const currentScale = this.container.scale.x as number;
              // const targetScale = to * currentScale;
              // const currentScale = this.sprite.scale.x as number;
              // const dx = ((from - to) * this._width * baseScale) / 2;
              const dx = ((from - to) * w) / 2;
              return target.x + dx + zoomDx;
            },
            y: (i, target) => {
              // const baseScale = this.container.scale.y as number;
              // const currentScale = this.sprite.scale.y as number;
              // const dy = ((currentScale - to) * this._height * baseScale) / 2;
              const dy = ((from - to) * h) / 2;
              return target.y + dy + zoomDy;
            },
            // ease: 'power1.inOut',
          }),
          0,
        );
      }

      if (to !== undefined) {
        timeline.add(
          gsap.to(this.container.scale, {
            duration: zoomDuration || duration || 1,
            x: to,
            y: to,
            // ease: 'power1.inOut',
          }),
          0,
        );
      }
    }

    if (translate) {
      const { from, to, duration: translateDuration } = translate;
      if (from) {
        const [x, y] = from;
        this.container.position.x += x;
        this.container.position.y += y;
      }

      if (from || to) {
        timeline.add(
          gsap.to(this.container.position, {
            duration: translateDuration || duration || 1,
            x: (i, target) => {
              return target.x + (to?.[0] || 0) - (from?.[0] || 0);
            },
            y: (i, target) => {
              return target.y + (to?.[1] || 0) - (from?.[1] || 0);
            },
            // ease: 'power1.inOut',
          }),
          0,
        );
      }
    }

    if (mask) {
      // -- //
      this.initMaskRectangle();
      this.maskRectangle?.patch({
        width: width,
        height: height,
        fill: '#000000',
        opacity: 0.001,
        r: 24,
      });

      let maskTween: gsap.core.Tween;
      switch (mask) {
        case 'left':
          this.maskRectangle.element.x = -width;
          maskTween = gsap.to(this.maskRectangle.element, {
            x: 0,
            duration: 0.4,
          });
          break;
        case 'right':
          this.maskRectangle.element.x = width;
          maskTween = gsap.to(this.maskRectangle.element, {
            x: 0,
            duration: 0.4,
          });
          break;
        case 'top':
          if (!this.maskRectangle) {
            return;
          }
          this.maskRectangle.element.y = -height;
          maskTween = gsap.to(this.maskRectangle.element, {
            y: 0,
            duration: 0.4,
          });
          break;
        case 'bottom':
          if (!this.maskRectangle) break;
          this.maskRectangle.element.y = height;
          maskTween = gsap.to(this.maskRectangle.element, {
            y: 0,
            duration: 0.4,
          });
          break;
      }

      if (maskTween) {
        timeline.add(maskTween, 0);
      }
      this.sprite.mask = this.maskRectangle.element;
    }

    if (gradientMask && type === 'appear') {
      this.container.alpha = 1;
      const isHorizontal = gradientMask === 'left' || gradientMask === 'right';
      const maskW = isHorizontal ? width * 2 : width;
      const maskH = isHorizontal ? height : height * 2;

      const tex = StudioImageShape.makeGradientTexture(gradientMask);
      const ms = new Sprite(tex);
      ms.width = maskW;
      ms.height = maskH;

      const blur = new BlurFilter();
      blur.blur = 26;
      blur.quality = 14;
      ms.filters = [blur];

      this.container.addChild(ms);
      this.sprite.mask = ms;
      this.gradientMaskSprite = ms;

      let startProp: 'x' | 'y';
      let startVal: number;

      switch (gradientMask) {
        case 'left':
          startProp = 'x';
          startVal = -maskW;
          break;
        case 'right':
          startProp = 'x';
          startVal = maskW;
          break;
        case 'top':
          startProp = 'y';
          startVal = -maskH;
          break;
        case 'bottom':
          startProp = 'y';
          startVal = maskH;
          break;
      }

      ms[startProp] = startVal;
      timeline.add(
        gsap.to(ms, { [startProp]: 0, duration: duration || 0.8, ease: 'power2.out' }),
        0,
      );
    }

    return super.gsapAnimateStep(animation, timeline);
  }

  private static makeGradientTexture(
    direction: 'left' | 'right' | 'top' | 'bottom',
  ): Texture {
    const isHorizontal = direction === 'left' || direction === 'right';
    const c = document.createElement('canvas');
    c.width = isHorizontal ? 256 : 2;
    c.height = isHorizontal ? 2 : 256;

    const ctx = c.getContext('2d');
    const g = isHorizontal
      ? ctx.createLinearGradient(0, 0, c.width, 0)
      : ctx.createLinearGradient(0, 0, 0, c.height);

    // Leading edge is opaque, trailing edge fades out
    if (direction === 'left' || direction === 'top') {
      g.addColorStop(0.0, 'rgba(255,255,255,1)');
      g.addColorStop(0.7, 'rgba(255,255,255,1)');
      g.addColorStop(0.85, 'rgba(255,255,255,0)');
      g.addColorStop(1.0, 'rgba(255,255,255,0)');
    } else {
      g.addColorStop(0.0, 'rgba(255,255,255,0)');
      g.addColorStop(0.15, 'rgba(255,255,255,0)');
      g.addColorStop(0.3, 'rgba(255,255,255,1)');
      g.addColorStop(1.0, 'rgba(255,255,255,1)');
    }

    ctx.fillStyle = g;
    ctx.fillRect(0, 0, c.width, c.height);

    return Texture.from(c);
  }
}
